import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_EMPLOYEE_URL, OWN_EMPLOYEE_POSITION_URL } from 'api/employee';
import {
    POSITION_CHANGE_REQUESTS_URL, MY_POSITION_CHANGE_REQUESTS_URL, cancelPositionChangeRequestUrl,
} from 'api/positionChangeRequest';
import { HTTP_METHODS } from 'constants';
import useHttp from 'hooks/useHttp';
import { getEmployeeId } from 'utils/auth';

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export default function useEmployeePosition({
    initialEmployee, employeeId, viewerId, enabled, onPositionRequestsChanged,
}) {
    const { sendRequest } = useHttp();
    const requestRef = useRef(sendRequest);
    requestRef.current = sendRequest;
    const [employee, setEmployee] = useState(initialEmployee);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(enabled && initialEmployee.active !== false);
    const [saving, setSaving] = useState(false);
    const [syncError, setSyncError] = useState('');
    const [mutationError, setMutationError] = useState('');
    const savingRef = useRef(false);
    const mounted = useRef(false);
    const generation = useRef(0);
    const readController = useRef(null);
    const active = initialEmployee.active !== false;

    const currentSession = useCallback(() =>
        mounted.current && getEmployeeId() === viewerId, [viewerId]);

    const refresh = useCallback(async () => {
        if (!enabled || !active || savingRef.current) {
            return;
        }
        const sequence = ++generation.current;
        readController.current?.abort();
        const controller = new AbortController();
        readController.current = controller;
        setLoading(true);
        const [position, requests] = await Promise.all([
            requestRef.current({ url: `${DEFAULT_EMPLOYEE_URL}/${employeeId}`, signal: controller.signal }),
            requestRef.current({ url: MY_POSITION_CHANGE_REQUESTS_URL, signal: controller.signal }),
        ]);
        if (!currentSession() || sequence !== generation.current) {
            return;
        }
        setLoading(false);
        if (position.ok && requests.ok && position.data?.id === employeeId && Array.isArray(requests.data?.data)) {
            setEmployee(position.data);
            setHistory(requests.data.data);
            setSyncError('');
        } else {
            setSyncError(errorMessage(position.error || requests.error,
                'Could not refresh your position and request status. Please try again.'));
        }
    }, [active, currentSession, employeeId, enabled]);

    useEffect(() => {
        mounted.current = true;
        refresh();
        const controller = readController.current;
        return () => {
            mounted.current = false;
            generation.current++;
            controller?.abort();
        };
    }, [refresh]);

    const mutate = useCallback(async (config, kind) => {
        if (!enabled || !currentSession() || savingRef.current || loading || saving || !!syncError || employee.active === false) {
            return { ok: false };
        }
        savingRef.current = true;
        generation.current++;
        readController.current?.abort();
        setSaving(true);
        setMutationError('');
        const result = await requestRef.current(config);
        if (!currentSession()) {
            return result;
        }
        savingRef.current = false;
        setSaving(false);
        if (result.ok) {
            onPositionRequestsChanged();
            if (kind === 'direct') {
                setEmployee((previous) => ({ ...previous, ...result.data }));
            } else {
                setHistory((previous) => [result.data,
                    ...previous.filter((request) => request.id !== result.data.id)].slice(0, 2));
            }
        } else {
            const conflict = result.error?.response?.status === 409;
            setMutationError(conflict
                ? kind === 'cancel'
                    ? 'This request changed or was already handled. Review the refreshed details.'
                    : 'Your position or request changed. Review the refreshed details. Close and reopen this dialog before trying again.'
                : errorMessage(result.error, 'The change could not be saved. Please try again.'));
        }
        return result;
    }, [currentSession, employee.active, enabled, loading, onPositionRequestsChanged, saving, syncError]);

    const submitRequest = useCallback((position) => mutate({
        url: POSITION_CHANGE_REQUESTS_URL, method: HTTP_METHODS.POST,
        data: { requestedPosition: position },
    }, 'request'), [mutate]);

    const updatePosition = useCallback((position, positionVersion) => mutate({
        url: OWN_EMPLOYEE_POSITION_URL, method: HTTP_METHODS.PUT,
        data: { position, expectedPositionVersion: positionVersion },
    }, 'direct'), [mutate]);

    const cancelRequest = useCallback((id, version) => mutate({
        url: cancelPositionChangeRequestUrl(id), method: HTTP_METHODS.POST,
        data: { version },
    }, 'cancel'), [mutate]);

    const pending = history.find((request) => request.status === 'Pending');
    const latestResolved = history.find((request) => request.status !== 'Pending');

    return {
        data: {
            employee,
            requests: { history, pending, latestResolved },
        },
        status: { loading, saving, syncError, mutationError },
        actions: {
            refresh,
            submitRequest,
            updatePosition,
            cancelRequest,
            clearMutationError: () => setMutationError(''),
        },
    };
}
